import React, { useEffect, useMemo, useState } from 'react';
import {
  AddUniversal,
  FilterOutlined,
  MoreInfoOutlined,
  TrashCanOutlined,
} from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Divider,
  Flex,
  InputNumber,
  notification,
  Select,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import { produce } from 'immer';
import { usePrevious } from 'react-use';
import styled from 'styled-components';

import { type MetricState } from '.';
import FullDivider from '../../common/FullDivider';
import { getDimensionsFromAnalysisConfig } from '../../helper/metric';
import { MetricFilter } from './MetricFilter';
import {
  Dimension,
  DraftDimensionFilterConstraint,
  MetricType,
  MetricWithDimensionFields,
} from './types';
import { constructDimensionFields } from './utils';
import Service, { Operators } from '@/lib/App';

const { Text } = Typography;

const JoinStrategyType = {
  TRANSACTING: 'TRANSACTING',
  EXPOSED: 'EXPOSED',
};
export type JoinStrategy =
  (typeof JoinStrategyType)[keyof typeof JoinStrategyType];

const MetricTypeToFieldMap: Record<MetricType, string> = {
  primary: 'primary_metrics',
  secondary: 'secondary_metrics',
  guardrail: 'guardrail_metrics',
};

type Props = {
  prefix?: string;
  filterEnabled: boolean;
  strategy: string;
  metricsGlossaryData: any[];
  metricType: MetricType;
  metricIndex: number;
  isViewMode: boolean;
  rawAnalysisConfig?: any;
  onSearch: (value: string) => void;
  options: React.ReactNode[];
  metrics: MetricState;
  setMetrics: React.Dispatch<React.SetStateAction<MetricState>>;
  onDelete: () => void;
  showValidation: boolean;
};

const MetricRow = ({
  prefix = '',
  filterEnabled,
  strategy,
  metricsGlossaryData,
  metricType,
  metricIndex,
  isViewMode,
  rawAnalysisConfig,
  onSearch,
  options,
  metrics,
  setMetrics,
  onDelete,
  showValidation,
}: Props) => {
  const [showFilter, setShowFilter] = useState(false);

  const metric: MetricWithDimensionFields | null = useMemo(() => {
    if (!metrics) {
      return null;
    }
    return metrics[MetricTypeToFieldMap[metricType]]?.[metricIndex] || null;
  }, [metricType, metricIndex, metrics]);
  const previousMetric = usePrevious(metric);

  const [dimensions, setDimensions] = useState<Dimension[] | null>(null);

  const [fetchingDimensions, setFetchingDimensions] = useState(false);

  const fetchDimensions = (metricName: string) => {
    setFetchingDimensions(true);

    if (isViewMode && rawAnalysisConfig) {
      const dimensions = getDimensionsFromAnalysisConfig(
        rawAnalysisConfig,
        metricName,
      );
      setDimensions(dimensions);
      setFetchingDimensions(false);
      return;
    }

    // For create/edit mode, use MIDAS API
    const fetchPromise = Service.planner
      .fetchDimensions(metricName, {
        purpose: 'filters',
        strategy,
        joinStrategy: metric?.is_exposed_join_enabled
          ? metric.join_strategy
          : null,
      })
      .then((d) => d.dimensions || []);

    fetchPromise
      .then((dimensions) => {
        setDimensions(dimensions);
      })
      .catch((e) => {
        setDimensions([]);
        notification.error({
          message: 'Error fetching dimensions',
          description: e?.message || 'Unknown error',
        });
      })
      .finally(() => {
        setFetchingDimensions(false);
      });
  };

  useEffect(() => {
    if (metric?.name && previousMetric?.name !== metric.name) {
      fetchDimensions(metric?.name);
    }
  }, [metric, metric?.join_strategy]);

  const noDimensionsForMetric = useMemo(() => {
    return dimensions && dimensions.length === 0;
  }, [dimensions]);

  const dimensionFilterConstraints = useMemo(() => {
    return metric?.dimension_filters?.[0]?.dimension_filter_constraints || [];
  }, [metric]);

  useEffect(() => {
    // Show existing filters when metric first initialized
    if (!previousMetric && metric && dimensionFilterConstraints.length) {
      setShowFilter(true);
    }
  }, [metric]);

  const metricFromGlossary = useMemo(() => {
    if (!metric?.name) return null;
    return metricsGlossaryData.find((m) => m.name === metric.name);
  }, [metric?.name, metricsGlossaryData]);

  const isExposedJoinEnabled =
    metricFromGlossary?.is_exposed_join_enabled ?? false;

  const handleJoinStrategyChange = (value: JoinStrategy) => {
    setMetrics((cur) =>
      produce(cur, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        draft[field][metricIndex].join_strategy = value;
        if (value === JoinStrategyType.TRANSACTING) {
          delete draft[field][metricIndex].metric_default_value;
        } else if (value === JoinStrategyType.EXPOSED) {
          draft[field][metricIndex].metric_default_value =
            draft[field][metricIndex].metric_default_value ?? 0;
        }
      }),
    );
  };

  const handleDefaultValueChange = (value: number | null) => {
    setMetrics((cur) =>
      produce(cur, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        draft[field][metricIndex].metric_default_value =
          value !== null && value !== undefined ? value : 0;
      }),
    );
  };

  const handleDeleteFilter = (index: number) => {
    setMetrics((cur) =>
      produce(cur, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        draft[field][
          metricIndex
        ].dimension_filters[0].dimension_filter_constraints.splice(index, 1);
      }),
    );
  };

  const handleShowFilterChange = () => {
    if (!showFilter) {
      setMetrics((cur) =>
        produce(cur, (draft) => {
          const field = MetricTypeToFieldMap[metricType];
          if (draft[field][metricIndex]) {
            draft[field][metricIndex].join_strategy = JoinStrategyType.EXPOSED;
            draft[field][metricIndex].metric_default_value =
              metric?.metric_default_value || 0;
          }
        }),
      );
    } else {
      setMetrics((cur) =>
        produce(cur, (draft) => {
          const field = MetricTypeToFieldMap[metricType];
          if (draft[field][metricIndex]) {
            delete draft[field][metricIndex].join_strategy;
            delete draft[field][metricIndex].metric_default_value;
          }
        }),
      );
    }

    setShowFilter((prev) => !prev);
  };

  const handleUpdateFilter = (
    index: number,
    constraint: DraftDimensionFilterConstraint,
  ) => {
    setMetrics((cur) =>
      produce(cur, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        draft[field][
          metricIndex
        ].dimension_filters[0].dimension_filter_constraints[index] = constraint;
      }),
    );
  };

  const handleAddFilter = () => {
    setMetrics((cur) =>
      produce(cur, (draft) => {
        const field = MetricTypeToFieldMap[metricType];
        draft[field][
          metricIndex
        ].dimension_filters[0].dimension_filter_constraints.push({
          dimension_name: null,
          operator: Operators.in,
          value: [],
        });
      }),
    );
  };

  if (!metric) {
    return null;
  }

  const renderFilter = () => {
    if (!dimensions?.length) {
      return null;
    }

    if (!isViewMode && !showFilter) {
      return null;
    }

    if (isViewMode && !dimensionFilterConstraints.length) {
      return null;
    }

    return (
      <FilterWrapper>
        <FullDivider
          orientation="left"
          orientationMargin={0}
          style={{ marginTop: 8, marginBottom: 8 }}
        >
          <Tag color="#3D3D3D" style={{ borderRadius: 8 }}>
            Filters
          </Tag>
        </FullDivider>
        <Flex vertical gap={8}>
          {dimensionFilterConstraints.map((constraint, index) => (
            <MetricFilter
              isViewMode={isViewMode}
              metricName={metric.name!}
              key={`filter-constraint-${index}-${constraint.dimension_name}`}
              constraint={constraint}
              dimensions={dimensions?.filter(
                (d) =>
                  d.name === constraint.dimension_name ||
                  !dimensionFilterConstraints.some(
                    (filter, i) =>
                      i !== index && filter.dimension_name === d.name,
                  ),
              )}
              onDelete={() => handleDeleteFilter(index)}
              onUpdate={(constraint) => handleUpdateFilter(index, constraint)}
              showValidation={showValidation}
            />
          ))}
          {!isViewMode &&
            dimensionFilterConstraints.length < dimensions?.length && (
              <Flex gap={8}>
                <AndTag>AND</AndTag>
                <ConfigProvider
                  theme={{
                    token: {
                      fontWeightStrong: 400,
                    },
                    components: {
                      Button: {
                        contentFontSize: 14,
                        defaultBorderColor: '#bfbfbf',
                      },
                    },
                  }}
                >
                  <Button
                    icon={<AddUniversal />}
                    onClick={handleAddFilter}
                    shape="default"
                  >
                    Add Filter
                  </Button>
                </ConfigProvider>
              </Flex>
            )}
        </Flex>
      </FilterWrapper>
    );
  };

  const renderFilterButton = () => {
    return noDimensionsForMetric ? (
      <Tooltip title="There are no filter dimensions for this metric">
        <Button
          size="small"
          shape="circle"
          icon={<FilterOutlined />}
          disabled
        />
      </Tooltip>
    ) : (
      <Button
        size="small"
        shape="circle"
        onClick={() => {
          handleShowFilterChange();
        }}
        icon={<FilterOutlined />}
        disabled={!metric?.name || fetchingDimensions}
      />
    );
  };

  const getMetricTagText = (index: number) => {
    if (metricType === 'primary') {
      return `P0`;
    }

    if (!prefix) {
      return index;
    }

    return `${prefix}-${index}`;
  };

  const renderJoinSettings = () => {
    const shouldShow =
      metric?.name &&
      (isViewMode ? metric.is_exposed_join_enabled : isExposedJoinEnabled) &&
      showFilter;

    if (!shouldShow) {
      return null;
    }

    return (
      <JoinSettingsWrapper>
        <Text strong style={{ marginTop: 8, marginBottom: 8 }}>
          Join Settings
        </Text>
        <Flex vertical gap={8}>
          <Flex vertical gap={4}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Text>Type:</Text>
              <Tooltip
                color="#FFF"
                title={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      backgroundColor: '#FFF',
                    }}
                  >
                    <Typography.Text>
                      <Typography.Text strong>Exposed</Typography.Text>:
                      Includes all exposed users in the experiment
                    </Typography.Text>
                    <Typography.Text>
                      <Typography.Text strong>Transacting</Typography.Text>:
                      Includes only transacting users present in metric table
                    </Typography.Text>
                    <Typography.Text>
                      to know more -{' '}
                      <a
                        href="https://docs.google.com/document/d/1P-ESVZqPZhSJa47j8MxFiBq59_zK34qdwB12rKyRrS8"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Release document
                      </a>
                    </Typography.Text>
                  </div>
                }
              >
                <MoreInfoOutlined />
              </Tooltip>
            </div>
            <Select
              style={{ width: 200 }}
              value={
                metric.join_strategy ||
                (isExposedJoinEnabled
                  ? JoinStrategyType.EXPOSED
                  : JoinStrategyType.TRANSACTING)
              }
              onChange={handleJoinStrategyChange}
              disabled={isViewMode}
              options={[
                { label: 'Transacting', value: JoinStrategyType.TRANSACTING },
                { label: 'Exposed', value: JoinStrategyType.EXPOSED },
              ]}
            />
          </Flex>
          {(metric.join_strategy === JoinStrategyType.EXPOSED ||
            (!metric.join_strategy && isExposedJoinEnabled)) && (
            <Flex vertical gap={4}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Text>Default Value:</Text>
                <Tooltip
                  color="#FFF"
                  title={
                    <Typography.Text>
                      In case of exposed user strategy, we populate this value
                      for null metrics
                    </Typography.Text>
                  }
                >
                  <MoreInfoOutlined />
                </Tooltip>
              </div>
              <InputNumber
                style={{ width: 200 }}
                precision={2}
                value={
                  metric.metric_default_value !== undefined
                    ? Number(metric.metric_default_value)
                    : 0
                }
                onChange={handleDefaultValueChange}
                placeholder="0"
                disabled={isViewMode}
              />
            </Flex>
          )}
        </Flex>
      </JoinSettingsWrapper>
    );
  };

  return (
    <MetricRowUI>
      <MetricTagUI>{getMetricTagText(metricIndex + 1)}</MetricTagUI>
      <Container>
        <Flex align="center" gap={8}>
          <Select
            showSearch
            showAction={['focus']}
            style={{ width: '100%' }}
            placeholder="Select Metric"
            dropdownRender={(menu) => {
              return (
                <>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0 8px',
                    }}
                  >
                    <div
                      style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                    >
                      <MoreInfoOutlined />
                      Analysis Unit (AU) is considered by GrabX to compute
                      scorecard for the metrics associated with the experiments
                    </div>
                    <Text strong style={{ paddingLeft: 10 }}>
                      Showing {metricsGlossaryData.length} results
                    </Text>
                  </div>
                </>
              );
            }}
            optionFilterProp="metadata.display_name"
            optionLabelProp="label"
            filterOption={false}
            onSearch={onSearch}
            onChange={(name) => {
              setMetrics((metrics) =>
                produce(metrics, (draft) => {
                  const field = MetricTypeToFieldMap[metricType];
                  draft[field][metricIndex] = constructDimensionFields(
                    metricsGlossaryData.find((m) => m.name === name),
                  );

                  if (!showFilter) {
                    delete draft[field][metricIndex].join_strategy;
                    delete draft[field][metricIndex].metric_default_value;
                  }
                }),
              );
              setShowFilter(false);
              onSearch('');
            }}
            suffixIcon={
              !!metric?.analysis_dimension && (
                <Tag
                  color="green"
                  size="xsmall"
                  style={{ height: 'fit-content' }}
                >
                  {`AU = ${metric?.analysis_dimension}`}
                </Tag>
              )
            }
            value={metric?.name}
            disabled={isViewMode}
          >
            {options}
          </Select>
          {!isViewMode && (
            <>
              <Button
                size="small"
                shape="circle"
                icon={<TrashCanOutlined />}
                disabled={!metric}
                onClick={onDelete}
              />
              {filterEnabled && renderFilterButton()}
            </>
          )}
        </Flex>
        {renderFilter()}
        {renderJoinSettings()}
      </Container>
    </MetricRowUI>
  );
};

export default MetricRow;

export const MetricTagUI = styled(Tag)<{ hasMetricID?: boolean }>`
  border-radius: 8px;
  border: none;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  min-width: 40px;
  width: ${({ hasMetricID }) => (hasMetricID ? '227px' : '40px')};
  align-items: center;
  justify-content: center;
  margin: 0;
`;

const MetricRowUI = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 0;
  align-items: stretch;
  min-width: 80%;
`;

const Container = styled.div`
  flex-grow: 1;
`;

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const JoinSettingsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AndTag = styled(Tag)`
  border-radius: 8px;
  background-color: #f5f5f5;
  color: #3d3d3d;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  margin-inline-end: 0px;
`;
